import { useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { shouldRegisterSw } from './swRoutes.js';

/**
 * Registro do service worker e estado da atualização.
 *
 * Duas decisões moram aqui, e as duas são sobre **não surpreender quem está
 * no meio de um evento**:
 *
 * 1. **Nunca recarrega sozinho.** `registerType: 'prompt'`: a versão nova
 *    fica esperando e a tela mostra um aviso. Recarregar sozinho faria o
 *    /controle perder o estado com o salão olhando.
 * 2. **A TV não registra service worker.** Navegador de smart TV é frágil, e
 *    um shell em cache numa TV ligada há semanas é o pior lugar para
 *    descobrir um bug. O escopo precisa ser `/` por causa do /inicio e do
 *    /controle, e escopo não aceita exclusão — então quem não registra é a
 *    própria página. Ver o cabeçalho de `sw.ts`.
 */

export interface PwaState {
  /** Há uma versão nova esperando para assumir. */
  updateAvailable: boolean;
  /** O app está pronto para funcionar offline (primeiro cache concluído). */
  offlineReady: boolean;
  /** Já está rodando instalado (standalone), e não numa aba do navegador. */
  standalone: boolean;
}

type Listener = () => void;

const listeners = new Set<Listener>();

let state: PwaState = Object.freeze({
  updateAvailable: false,
  offlineReady: false,
  standalone: detectStandalone(),
});

/**
 * Snapshot novo e **congelado** a cada mudança.
 *
 * Mesma regra de `session.ts`: publicar a mesma referência faria o React
 * descartar a atualização com `Object.is` e o aviso nunca apareceria. Já
 * aconteceu uma vez neste projeto; não acontece de novo.
 */
function publish(patch: Partial<PwaState>): void {
  state = Object.freeze({ ...state, ...patch });
  for (const listener of listeners) listener();
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    // Safari no iOS não implementa o media query; usa esta propriedade.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** Aplica a atualização que está esperando. Só por toque explícito. */
let applyUpdate: (() => void) | null = null;

export function updateApp(): void {
  applyUpdate?.();
}

let registered = false;

export function setupPwa(): void {
  if (registered || typeof window === 'undefined') return;
  if (!shouldRegisterSw(window.location.pathname)) return;

  registered = true;

  const update = registerSW({
    immediate: true,
    onNeedRefresh() {
      publish({ updateAvailable: true });
    },
    onOfflineReady() {
      publish({ offlineReady: true });
    },
  });

  // `true` = recarrega a página depois de o novo service worker assumir. É
  // o comportamento certo AQUI porque só chegamos aqui por um toque.
  applyUpdate = () => void update(true);
}

export function subscribePwa(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function pwaSnapshot(): PwaState {
  return state;
}

export function usePwa(): PwaState {
  return useSyncExternalStore(subscribePwa, pwaSnapshot, pwaSnapshot);
}

/** Só para os testes. */
export function resetPwaState(): void {
  registered = false;
  applyUpdate = null;
  state = Object.freeze({ updateAvailable: false, offlineReady: false, standalone: false });
}
